import { useSettingsContext } from '@/context/settings'
import { cn } from '@/utils/cn'
import { useState } from 'react'
import { Button } from '../common/button'
import { Label } from '../common/label'
import { Popover, PopoverContent, PopoverTrigger } from '../common/popover'
import { Separator } from '../common/separator'
import { Switch } from '../common/switch'
import { ArrowIcon } from '../icons/arrow'
import { SettingsIcon } from '../icons/settings'
import { ApyRangeSettingsSelector } from '../shared/apyRangeSettingsSelector'
import { AppearanceSettings } from './appearanceSettings'
import { TvlSettings } from './tvlSettings'

interface Props {
  className?: string
  modal?: boolean
}

type SettingsTab = 'select' | 'tvl'
const tabButton = cn('flex h-12 w-full items-center justify-between gap-2 border-t px-4 py-3 first:border-transparent')

export const SettingsMenu = ({ className, modal }: Props) => {
  const [tab, setTab] = useState<SettingsTab>('select')
  const [hasSwitchedTab, setHasSwitchedTab] = useState(false)

  const { includeRewards, handleIncludeRewards } = useSettingsContext()

  const handleTabSwitch = (newTab: SettingsTab) => {
    setHasSwitchedTab(true)
    setTab(newTab)
  }

  const handleMenuClose = () => {
    setHasSwitchedTab(false)
    setTab('select')
  }

  return (
    <Popover modal={modal} onOpenChange={handleMenuClose}>
      <PopoverTrigger asChild>
        <Button variant="input" size="md" className={cn('flex items-center justify-center gap-2', className)}>
          <SettingsIcon />
          <span>Settings</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[var(--radix-popover-trigger-width)] xl:w-72">
        {tab === 'select' && (
          <div
            className={cn(
              hasSwitchedTab && tab === 'select' && 'fade-in slide-in-from-left animate-in',
              tab !== 'select' && 'fade-out slide-out-to-left animate-out'
            )}
          >
            <div className="flex flex-col gap-4 px-4 py-3">
              <AppearanceSettings />
              <Separator />
              <div className="hocus-within:text-accent text-sm transition-color">
                <span>Show APY as avg. of</span>
                <ApyRangeSettingsSelector className="mt-1" />
              </div>
              <Separator />
              <div>
                <Label className="text-sm">
                  <Switch checked={includeRewards} onCheckedChange={handleIncludeRewards} />
                  Include rewards
                </Label>
              </div>
            </div>
            <Button variant="default" className={tabButton} onClick={() => handleTabSwitch('tvl')}>
              TVL settings
              <ArrowIcon direction="right" className="ml-auto" />
            </Button>
          </div>
        )}
        {tab === 'tvl' && (
          <TvlSettings
            onReturn={() => handleTabSwitch('select')}
            className={cn(
              tab === 'tvl' && 'fade-in slide-in-from-right animate-in',
              tab !== 'tvl' && 'fade-out slide-out-to-right animate-out'
            )}
          />
        )}
      </PopoverContent>
    </Popover>
  )
}
